import React, { useMemo } from 'react';
import { Filter } from '@packages/ui/composites/Filter';
import { FilterBottomSheet } from '@packages/ui/composites/Filter';
import type { FilterPanelSize } from '@packages/ui/composites/Filter';
import { Button } from '@packages/ui/primitives/Button';
import { Typography } from '@packages/ui/primitives/Typography';
import { TextInput } from '@packages/ui/primitives/TextInput';
import { Select } from '@packages/ui/composites/Select';
import type { SelectOption } from '@packages/ui/composites/Select';
import { useMediaQuery } from '@packages/ui/internals';
import type { FilterDef } from '../schema/types';
import {
  AsyncFilterSelect as RenderAsyncFilterSelect,
  DateFilter as RenderV2DateFilter,
  DateRangeFilter as RenderV2DateRangeFilter,
} from './filters';
import { TABLE_IDS } from '../semantic-contract';

function derivePanelSize(filterCount: number, columns: number): FilterPanelSize {
  if (columns >= 3 || filterCount > 6) return 'lg';
  if (columns >= 2 || filterCount > 2) return 'md';
  return 'sm';
}

interface FilterPanelProps {
  filterDefs: readonly FilterDef[];
  isFilterOpen: boolean;
  setIsFilterOpen: (open: boolean) => void;
  pendingFilter: Record<string, unknown>;
  handleFilterChange: (name: string, value: unknown) => void;
  handleApplyFilter: () => void;
  handleAsyncOptionsLoaded: (
    name: string,
    options: Array<{ label: string; value: string | number }>
  ) => void;
  axiosInstance?: any;
  /** Number of grid columns in the filter panel (default 2 on desktop, 1 on mobile). */
  filterGridColumns?: number;
}

function StaticFilterSelect({
  filter,
  value,
  onFilterChange,
  disabled,
}: {
  filter: FilterDef;
  value: unknown;
  onFilterChange: (name: string, value: unknown) => void;
  disabled: boolean;
}): JSX.Element {
  const options: SelectOption[] = useMemo(
    () => (filter.options ?? []).map((o) => ({ label: String(o.label), value: String(o.value) })),
    [filter.options],
  );

  const currentValue = useMemo(() => {
    if (value === undefined || value === null || value === '') {
      return filter.multiple ? [] : null;
    }
    if (filter.multiple && Array.isArray(value)) {
      return value
        .map((v) => options.find((o) => String(o.value) === String(v)))
        .filter(Boolean) as SelectOption[];
    }
    return options.find((o) => String(o.value) === String(value)) ?? null;
  }, [value, options, filter.multiple]);

  return (
    <Select
      name={filter.name}
      placeholder={filter.placeholder}
      value={currentValue as any}
      onChange={(selected: any) => {
        if (filter.multiple) {
          const vals = Array.isArray(selected) ? selected.map((s: any) => s.value) : [];
          onFilterChange(filter.name, vals.length > 0 ? vals : undefined);
        } else {
          onFilterChange(filter.name, selected?.value ?? undefined);
        }
      }}
      options={options}
      isMulti={filter.multiple}
      isDisabled={disabled}
      isClearable
      semanticId={TABLE_IDS.filterSelect(filter.name)}
    />
  );
}

// ─── Shared filter content (used by both desktop popover and mobile sheet) ───

interface FilterContentProps {
  filterDefs: readonly FilterDef[];
  pendingFilter: Record<string, unknown>;
  handleFilterChange: (name: string, value: unknown) => void;
  handleApplyFilter: () => void;
  handleAsyncOptionsLoaded: (
    name: string,
    options: Array<{ label: string; value: string | number }>
  ) => void;
  axiosInstance?: any;
  columns?: number;
}

function FilterContent({
  filterDefs,
  pendingFilter,
  handleFilterChange,
  handleApplyFilter,
  handleAsyncOptionsLoaded,
  axiosInstance,
  columns = 2,
}: FilterContentProps): JSX.Element {
  return (
    <>
      <Filter.Header>Filter By:</Filter.Header>
      <Filter.Grid columns={columns}>
        {filterDefs.map((f) => {
          const depMet = !f.dependsOn || Boolean(pendingFilter[f.dependsOn]);
          const rowKey = f.type === 'dateRange' ? `${f.name}__dateRange` : f.name;

          const control =
            f.type === 'select' ? (
              <StaticFilterSelect
                filter={f}
                value={pendingFilter[f.name]}
                onFilterChange={handleFilterChange}
                disabled={!depMet}
              />
            ) : f.type === 'asyncSelect' ? (
              <RenderAsyncFilterSelect
                filter={f}
                value={pendingFilter[f.name]}
                onFilterChange={handleFilterChange}
                axiosInstance={axiosInstance}
                disabled={!depMet}
                onOptionsLoaded={handleAsyncOptionsLoaded}
              />
            ) : f.type === 'date' ? (
              <RenderV2DateFilter
                filter={f}
                value={pendingFilter[f.name]}
                onFilterChange={handleFilterChange}
                pendingFilter={pendingFilter}
              />
            ) : f.type === 'dateRange' ? (
              <RenderV2DateRangeFilter
                filter={f}
                pendingFilter={pendingFilter}
                onFilterChange={handleFilterChange}
              />
            ) : (
              <TextInput
                type={f.type === 'number' ? 'number' : 'text'}
                placeholder={f.placeholder}
                value={
                  pendingFilter[f.name] === undefined || pendingFilter[f.name] === null
                    ? ''
                    : String(pendingFilter[f.name])
                }
                onChange={(e) =>
                  handleFilterChange(
                    f.name,
                    f.type === 'number'
                      ? e.target.value === ''
                        ? undefined
                        : Number(e.target.value)
                      : e.target.value || undefined
                  )
                }
                semanticId={TABLE_IDS.filterInput(f.name)}
                disabled={!depMet}
              />
            );

          const wrappedControl = f.semanticId ? <div id={f.semanticId}>{control}</div> : control;

          const span = f.colSpan && f.colSpan > 1 ? f.colSpan : undefined;

          return (
            <div
              key={rowKey}
              className='flex flex-col'
              data-testid={`dt-v2-filter-${f.name}`}
              style={span ? { gridColumn: `span ${span}` } : undefined}
            >
              <Typography variant='body-field' style={{ marginBottom: 8 }}>
                {f.placeholder}
              </Typography>
              {wrappedControl}
            </div>
          );
        })}
      </Filter.Grid>
      <Filter.Actions>
        <Button
          variant='primary'
          label='Filter'
          onClick={handleApplyFilter}
          semanticId={TABLE_IDS.filterButton}
          className='w-full sm:w-auto'
        />
      </Filter.Actions>
    </>
  );
}

// ─── Main export ─────────────────────────────────────────────────────────────

export function FilterPanel({
  filterDefs,
  isFilterOpen,
  setIsFilterOpen,
  pendingFilter,
  handleFilterChange,
  handleApplyFilter,
  handleAsyncOptionsLoaded,
  axiosInstance,
  filterGridColumns,
}: FilterPanelProps): JSX.Element | null {
  const isMobile = useMediaQuery('(max-width: 639px)');

  if (filterDefs.length === 0) return null;

  const columns = isMobile ? 1 : (filterGridColumns ?? 2);
  const panelSize = derivePanelSize(filterDefs.length, columns);

  const sharedContent = (
    <FilterContent
      filterDefs={filterDefs}
      pendingFilter={pendingFilter}
      handleFilterChange={handleFilterChange}
      handleApplyFilter={handleApplyFilter}
      handleAsyncOptionsLoaded={handleAsyncOptionsLoaded}
      axiosInstance={axiosInstance}
      columns={columns}
    />
  );

  if (isMobile) {
    return (
      <>
        <Filter.Root open={isFilterOpen} onOpenChange={setIsFilterOpen}>
          <Filter.Trigger semanticId={TABLE_IDS.filterTrigger} />
        </Filter.Root>
        <FilterBottomSheet open={isFilterOpen} onClose={() => setIsFilterOpen(false)}>
          {sharedContent}
        </FilterBottomSheet>
      </>
    );
  }

  return (
    <Filter.Root open={isFilterOpen} onOpenChange={setIsFilterOpen}>
      <Filter.Trigger semanticId={TABLE_IDS.filterTrigger} />
      <Filter.Panel size={panelSize}>
        {sharedContent}
      </Filter.Panel>
    </Filter.Root>
  );
}
